<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import UploadField from "@/components/UploadField.vue";
import { uploadImage } from "@/lib/uploads";
import { fetchService, addService, updateService } from "./api";

const route = useRoute(), router = useRouter();
const id = computed(() => route.params.id || null);
const isEdit = computed(() => !!id.value);
const loading = ref(false), notFound = ref(false), saving = ref(false), success = ref(false), error = ref(""), validationError = ref("");

const pageLinkItems = ["Kronos", "IHP", "Success Factor", "Concur", "Zendesk", "Videos", "Campaign", "Video Campaign", "Wheel", "Recognition Stamps", "More Options"];
const form = ref({ imageUrl: "", title: "", pageLink: "", redirectUrl: "", row: "", column: "", isEnabled: true, isMoreServices: false, isExternalBrowser: false, isDynamic: false });
const imageUploader = (file, p) => uploadImage("services", file, p);

onMounted(async () => {
  if (!isEdit.value) return;
  loading.value = true;
  try {
    const s = await fetchService(id.value);
    if (!s) { notFound.value = true; return; }
    form.value = { imageUrl: s.imageUrl ?? "", title: s.title ?? "", pageLink: s.pageLink ?? "", redirectUrl: s.redirectUrl ?? "", row: s.row ?? "", column: s.column ?? "", isEnabled: s.isEnabled ?? true, isMoreServices: !!s.isMoreServices, isExternalBrowser: !!s.isExternalBrowser, isDynamic: !!s.isDynamic };
  } catch { error.value = "Couldn't load the service."; }
  finally { loading.value = false; }
});

async function save() {
  success.value = false; error.value = ""; validationError.value = "";
  const f = form.value;
  if (!f.title.trim()) { validationError.value = "Service name is required."; return; }
  const hasLink = !!f.pageLink, hasUrl = !!f.redirectUrl.trim();
  if (hasLink === hasUrl) { validationError.value = "Provide either a Page Link OR a Page URL — not both."; return; }
  if (!f.imageUrl) { validationError.value = "Please select an icon."; return; }
  if (f.row === "" || f.column === "") { validationError.value = "Row and Column are required."; return; }
  saving.value = true;
  try {
    if (isEdit.value) await updateService(id.value, { ...f }); else await addService({ ...f });
    success.value = true; setTimeout(() => router.push({ name: "services" }), 800);
  } catch { error.value = "Error occurred. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'services' })">Back</VBtn>
    <h1 class="text-h5 mb-4">{{ isEdit ? "Edit Service" : "Add Service" }}</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Service not found.</VAlert>
    <VCard v-else class="pa-4" max-width="700">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved. Redirecting…</VAlert>
      <form @submit.prevent="save">
        <UploadField v-model="form.imageUrl" kind="image" label="Icon" :uploader="imageUploader" class="mb-4" />
        <VRow>
          <VCol cols="12"><VTextField v-model="form.title" label="Service Name" /></VCol>
          <VCol cols="12" md="6"><VSelect v-model="form.pageLink" label="Page Link (preset)" :items="pageLinkItems" clearable placeholder="—" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.redirectUrl" label="Page URL" placeholder="https://example.com/" /></VCol>
          <VCol cols="6"><VSwitch v-model="form.isDynamic" label="Dynamic Link" hide-details /></VCol>
          <VCol cols="6"><VSwitch v-model="form.isExternalBrowser" label="External Browser" hide-details /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.row" label="Row" type="number" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.column" label="Column" type="number" /></VCol>
          <VCol cols="6"><VSwitch v-model="form.isEnabled" label="Enabled" hide-details /></VCol>
          <VCol cols="6"><VSwitch v-model="form.isMoreServices" label="Include in More Services" hide-details /></VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mb-4 mt-2">{{ validationError }}</VAlert>
        <div class="d-flex gap-2 mt-4">
          <VBtn type="submit" :loading="saving">Submit</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'services' })">Cancel</VBtn>
        </div>
      </form>
    </VCard>
  </section>
</template>
